type TokenProvider = () => Promise<string>;

let tokenProvider: TokenProvider | null = null;
const pendingAuthCallbacks: Array<() => void> = [];

export function configureTokenProvider(fn: TokenProvider): void {
  tokenProvider = fn;
  // Flush any requests that arrived before auth was ready
  const pending = pendingAuthCallbacks.splice(0);
  pending.forEach(cb => cb());
}

export interface ApiCallbacks<T> {
  onSuccess: (data: T) => void;
  onNotFound?: () => void;
  onBadRequest?: (message: string) => void;
  onError?: (message: string) => void;
}

function waitForTokenProvider(): Promise<TokenProvider> {
  if (tokenProvider) return Promise.resolve(tokenProvider);
  return new Promise(resolve => {
    pendingAuthCallbacks.push(() => resolve(tokenProvider!));
  });
}

async function buildHeaders(authenticated: boolean): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (authenticated) {
    const provider = await waitForTokenProvider();
    headers['Authorization'] = `Bearer ${await provider()}`;
  }
  return headers;
}

async function dispatch<T>(res: Response, callbacks: ApiCallbacks<T>, acceptErrorBody = false): Promise<void> {
  if (res.ok) {
    const text = await res.text();
    callbacks.onSuccess(text ? JSON.parse(text) : undefined as unknown as T);
  } else if (res.status === 404) {
    callbacks.onNotFound?.();
  } else if (res.status === 400) {
    callbacks.onBadRequest?.(await res.text());
  } else if (acceptErrorBody) {
    const text = await res.text();
    try {
      callbacks.onSuccess(JSON.parse(text) as T);
    } catch {
      callbacks.onError?.(`Error ${res.status}: ${res.statusText}`);
    }
  } else {
    callbacks.onError?.(`Error ${res.status}: ${res.statusText}`);
  }
}

export async function apiGet<T>(
  url: string,
  callbacks: ApiCallbacks<T>,
  authenticated = false,
  acceptErrorBody = false
): Promise<void> {
  try {
    const res = await fetch(url, { headers: await buildHeaders(authenticated) });
    await dispatch(res, callbacks, acceptErrorBody);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}

export async function apiPost<T>(
  url: string,
  body: unknown,
  callbacks: ApiCallbacks<T>,
  authenticated = false
): Promise<void> {
  try {
    const headers = { ...await buildHeaders(authenticated), 'Content-Type': 'application/json' };
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    await dispatch(res, callbacks);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}

export async function apiPut<T>(
  url: string,
  body: unknown,
  callbacks: ApiCallbacks<T>,
  authenticated = false
): Promise<void> {
  try {
    const headers = { ...await buildHeaders(authenticated), 'Content-Type': 'application/json' };
    const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) });
    await dispatch(res, callbacks);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}

export async function apiDelete<T>(
  url: string,
  callbacks: ApiCallbacks<T>,
  authenticated = false
): Promise<void> {
  try {
    const res = await fetch(url, { method: 'DELETE', headers: await buildHeaders(authenticated) });
    await dispatch(res, callbacks);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}

// Multipart form data helpers — used for endpoints that accept file uploads.
// Do NOT set Content-Type manually; the browser injects the multipart boundary automatically.

export async function apiPostForm<T>(
  url: string,
  formData: FormData,
  callbacks: ApiCallbacks<T>,
  authenticated = false
): Promise<void> {
  try {
    const res = await fetch(url, { method: 'POST', headers: await buildHeaders(authenticated), body: formData });
    await dispatch(res, callbacks);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}

export async function apiPostDownload(
  url: string,
  body: unknown,
  fallbackFileName: string,
  onError: (message: string) => void,
  authenticated = false
): Promise<void> {
  try {
    const headers = { ...await buildHeaders(authenticated), 'Content-Type': 'application/json' };
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) {
      onError(`Error ${res.status}: ${res.statusText}`);
      return;
    }
    const blob = await res.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    const disposition = res.headers.get('content-disposition');
    const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    a.download = match ? match[1].replace(/['"]/g, '') : fallbackFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  } catch (err: any) {
    onError(err.message ?? 'Error desconocido');
  }
}

export async function apiPutForm<T>(
  url: string,
  formData: FormData,
  callbacks: ApiCallbacks<T>,
  authenticated = false
): Promise<void> {
  try {
    const res = await fetch(url, { method: 'PUT', headers: await buildHeaders(authenticated), body: formData });
    await dispatch(res, callbacks);
  } catch (err: any) {
    callbacks.onError?.(err.message ?? 'Error desconocido');
  }
}
